import React, { useState, useEffect } from 'react'
import { Button, Form, InputGroup, Modal, Toast } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { useTranslation } from 'react-i18next';
import { generateUpdateKeyVaultCertificatePinUri, putManagementSignature } from '../../../services/signatureMethods/customServiceSignature';
import LoadingCircle from '../../LoadingCircle';
import Utils from '../../../services/Utils';
import IconButton from '../../IconButton/IconButton';
import { ReactComponent as PasswordIcon } from '../../../assets/icons/password.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/trash.svg';
import plusIcon from '../../../assets/icons/plus-button.png';
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { credentialNamespace } from '../../../services/SocketService';
import CreateVC from '../CreateVC/CreateVC';
// import vcService from '../../../services/vcService';

const INITIAL_PIN_DATA = {
  currentPin: '',
  newPin: '',
  confirmNewPin: ''
};

const KeyVaultCertificates = ({ parentState, onHandleFileSelection, onUploadCertificateKeyVault, onDeleteKeyVault, onFileInput, onHandleKeyVaultChange, onSuccessPinChanged, onError, onRefreshCertificates }) => {
  const { t } = useTranslation();
  const {
    certificatesOrdered: { KeyVault },
    KVpwIsInvalid,
    errorMessageKVpw,
    fileName,
    KValias,
    KVpassword,
    KVpasswordConfirmation,
  } = parentState;

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [updatePinData, setUpdatePinData] = useState(INITIAL_PIN_DATA);
  const [loading, setLoading] = useState(false);
  const [showCreateNewCertificateModal, setShowCreateNewCertificateModal] = useState(false);
  const [walletToggleLoadingByGuid, setWalletToggleLoadingByGuid] = useState({});
  const [walletToggleError, setWalletToggleError] = useState(false);
  const [walletToggleSuccess, setWalletToggleSuccess] = useState(false);
  
  // const [createVCTriggers, setCreateVCTriggers] = useState({});
  const [createVCTriggers] = useState({});

  const handleCloseCreateNewCertificateModal = () => setShowCreateNewCertificateModal(false);

  const { currentPin, newPin, confirmNewPin } = updatePinData;

  const isUpdatePinButtonDisabled = !currentPin || !newPin || !confirmNewPin || newPin !== confirmNewPin;

  const selectCertificateToUpdate = (row) => {
    setSelectedCertificate(row);
  }

  const handleUpdatePinDataChange = (e) => {
    const { name, value } = e.target;

    setUpdatePinData({
      ...updatePinData,
      [name]: value
    });
  };

  const handleHide = () => {
    setSelectedCertificate(null);
    setUpdatePinData(INITIAL_PIN_DATA);
  }

  const handleUpdatePin = () => {
    const body = {
      CurrentPin: currentPin,
      NewPin: newPin,
    };

    setLoading(true);

    const { oauthTokenB64 } = parentState;

    const userGuid = Utils.getViDMobileUserGUIDFromOauthToken(oauthTokenB64);

    putManagementSignature(generateUpdateKeyVaultCertificatePinUri(userGuid, selectedCertificate.GUID), body, oauthTokenB64)
      .then(() => {
        setLoading(false);
        setSelectedCertificate(null);
        onHandleKeyVaultChange("KVpassword", newPin, false);
        setUpdatePinData(INITIAL_PIN_DATA);
        setSelectedCertificate(null);
        onSuccessPinChanged();
      })
    .catch((error) => {
      setLoading(false);
      setSelectedCertificate(null);
      onError(error.response);
    });
  }

  if (KeyVault.length === 0) {
    KeyVault.push({
      GUID: 'placeholder',
      certalias: '---',
      ExpirationDate: '---',
    })
  }


  // Listen for credentialStatus events to update toggle
  useEffect(() => {
    const eventStatusCallback = (credentialStatus) => {
      const { status, certificateGuid } = credentialStatus;

      // Avoid overriding UI while an unlink/revocation request is in-flight
      if (certificateGuid && walletToggleLoadingByGuid[certificateGuid]) return;
      
      let normalizedStatus = status.toLowerCase();
      const mappedStatus = (normalizedStatus === 'ok' || normalizedStatus === 'success' || normalizedStatus === 'issued')
        ? 'approved'
        : 'rejected';

      if (mappedStatus === 'approved' && certificateGuid) {
        // Mark toggle as active when VC is successfully issued
        const updatedCertificates = KeyVault.map(cert => 
          cert.GUID === certificateGuid 
            ? { ...cert, WalletAuthorization: true }
            : cert
        );
        if (onHandleKeyVaultChange) {
          onHandleKeyVaultChange("KeyVault", updatedCertificates, false);
        }

        // Unlock toggle after backend confirmation
        setWalletToggleLoading(certificateGuid, false);
      } else if (mappedStatus === 'rejected' && certificateGuid) {
        // Reset toggle to off state on rejection
        const updatedCertificates = KeyVault.map(cert => 
          cert.GUID === certificateGuid 
            ? { ...cert, WalletAuthorization: false }
            : cert
        );
        if (onHandleKeyVaultChange) {
          onHandleKeyVaultChange("KeyVault", updatedCertificates, false);
        }

        // Unlock toggle after backend confirmation
        setWalletToggleLoading(certificateGuid, false);
      }
    };

    const handleVCIssued = (data) => {
      eventStatusCallback({ status: 'issued', certificateGuid: data.certificateGuid });
    };

    const handleVCRejected = (data) => {
      eventStatusCallback({ status: 'rejected', certificateGuid: data.certificateGuid });
    };

    credentialNamespace.on('credentialStatus', eventStatusCallback);
    credentialNamespace.on('vc:issued', handleVCIssued);
    credentialNamespace.on('vc:rejected', handleVCRejected);

    return () => {
      credentialNamespace.off('credentialStatus', eventStatusCallback);
      credentialNamespace.off('vc:issued', handleVCIssued);
      credentialNamespace.off('vc:rejected', handleVCRejected);
    };
  }, [KeyVault, onHandleKeyVaultChange, walletToggleLoadingByGuid]);

  const setWalletToggleLoading = (certificateGuid, value) => {
    setWalletToggleLoadingByGuid(prev => ({
      ...prev,
      [certificateGuid]: value,
    }));
  };

  // const handleWalletAuthorizationToggle = (row, checked) => {
  //   if (row.CertType !== 'KeyVault') {
  //     return;
  //   }

  //   if (walletToggleLoadingByGuid[row.GUID]) return;

  //   if (!checked) {
  //     // Turning OFF => revoke/unlink credentials via backend
  //     const { oauthTokenB64 } = parentState;
  //     const userGuid = oauthTokenB64 ? Utils.getViDMobileUserGUIDFromOauthToken(oauthTokenB64) : null;

  //     // Optimistic UI update (OFF) + block interactions
  //     const updatedCertificates = KeyVault.map(cert =>
  //       cert.GUID === row.GUID
  //         ? { ...cert, WalletAuthorization: false }
  //         : cert
  //     );
  //     if (onHandleKeyVaultChange) onHandleKeyVaultChange("KeyVault", updatedCertificates, false);

  //     setWalletToggleError(null);
  //     setWalletToggleLoading(row.GUID, true);

  //     vcService
  //       .revokeCertificateCredentials(userGuid, row.GUID)
  //       .then(() => {
  //         setWalletToggleSuccess(true);
  //         setTimeout(() => {
  //           onRefreshCertificates();
  //         }, 700);
  //       })
  //       .catch((error) => {
  //         // Revert UI toggle back to ON
  //         const revertedCertificates = KeyVault.map(cert =>
  //           cert.GUID === row.GUID
  //             ? { ...cert, WalletAuthorization: true }
  //             : cert
  //         );
  //         if (onHandleKeyVaultChange) onHandleKeyVaultChange("KeyVault", revertedCertificates, false);

  //         const responseData = error && error.response && error.response.data ? error.response.data : null;
  //         const message = error && error.message ? error.message : null;
  //         setWalletToggleError(responseData || message || 'Error revoking credentials');
  //       })
  //       .finally(() => {
  //         setWalletToggleLoading(row.GUID, false);
  //       });

  //     return;
  //   }

  //   // If turning on, trigger CreateVC modal
  //   // Lock toggle to prevent multiple activations until backend confirms via socket events.
  //   setWalletToggleLoading(row.GUID, true);
  //   setCreateVCTriggers(prev => ({
  //     ...prev,
  //     [row.GUID]: Date.now() // Use timestamp to trigger the modal
  //   }));

  //   // Safety unlock: if no socket event arrives (user cancels / network issues), re-enable after 2 minutes
  //   setTimeout(() => {
  //     setWalletToggleLoading(row.GUID, false);
  //   }, 2 * 60 * 1000);
  // };

  const KeyVaultColumns = [
    {
      dataField: 'certalias',
      text: t('certificates.certificate'),
      align: 'left',
      sort: true,
      headerAlign: 'left',
      headerClasses: 'headerClass1',
      style: { verticalAlign: 'middle' }
    },
    {
      dataField: 'ExpirationDate',
      text: t('certificates.certificate-date'),
      align: 'left',
      sort: true,
      headerAlign: 'left',
      headerClasses: 'headerClass2',
      style: { verticalAlign: 'middle' }
    },
    // {
    //   dataField: 'WalletAuthorization',
    //   text: t('certificates.wallet-authorization'),
    //   align: 'center',
    //   headerAlign: 'center',
    //   isDummyField: true,
    //   style: { verticalAlign: 'middle' },
    //   headerClasses: 'headerClass4',
    //   formatter: (_, row) => {
    //     return row.certalias === "---" ? "---" : (
    //       <div className="wallet-auth-toggle-container">
    //         <label className="switch">
    //             <input
    //               type="checkbox"
    //               checked={row.WalletAuthorization || false}
    //               disabled={!!walletToggleLoadingByGuid[row.GUID]}
    //               onChange={(e) => handleWalletAuthorizationToggle(row, e.target.checked)}
    //             />
    //             <span className="slider round"></span>
    //           </label>
    //         </div>
    //     );
    //   }
    // },
    {
      dataField: 'ActionButton',
      text: t('general.actions'),
      align: 'center',
      headerAlign: 'left',
      isDummyField: true,
      style: { verticalAlign: 'middle' },
      headerClasses: 'headerClass3',
      formatter: (_, row) => {
        return row.certalias === "---" ? "---" : (
            <div className='keyvault-buttons-container'>
              <IconButton
                icon={PasswordIcon}
                tooltipText={t('certificates.change-pin')}
                onClick={() => selectCertificateToUpdate(row)}
              />
              <IconButton
                icon={DeleteIcon}
                tooltipText={t('certificates.certificate-delete')}
                onClick={() => onDeleteKeyVault(true, row)}
              />
            </div>
        );
      }
    }
  ];
  return (
    <>
      <div className="table-container">
        <div className='table-container-header'>
          <button className='new-create-button' onClick={() => setShowCreateNewCertificateModal(true)}>
            <img src={plusIcon} alt="plus icon" />
            <span>
              {t('certificates.create-certificate')}
            </span>
          </button>
        </div>
          <div>
            <ToolkitProvider
              keyField="GUID"
              data={KeyVault}
              columns={KeyVaultColumns}
            >
              {
                props => (
                  <div>
                    <BootstrapTable striped
                      {...props.baseProps}
                    />
                  </div>
                )
              }
            </ToolkitProvider>
          </div>
      </div>
      <Modal show={!!selectedCertificate} onHide={handleHide}>
        <Modal.Header closeButton>
          <Modal.Title>{t('certificates.change-pin-modal')}</Modal.Title>
        </Modal.Header>
        {
          loading ? <LoadingCircle /> : 
          <>
            <Modal.Body>
              <InputGroup className="mb-3">
                  <InputGroup.Prepend>
                      <InputGroup.Text
                          id="inputGroup-sizing-default">{t('certificates.current-pin')}:
                      </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name='currentPin'
                    value={currentPin}
                    required
                    type="password"
                    aria-label="Default"
                    aria-describedby="inputGroup-sizing-default"
                    autoComplete="new-password"
                    onChange={handleUpdatePinDataChange}
                  />
              </InputGroup>
              <InputGroup className="mb-3">
                  <InputGroup.Prepend>
                      <InputGroup.Text
                          id="inputGroup-sizing-default">{t('certificates.new-pin')}:
                      </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name='newPin'
                    value={newPin}
                    required
                    type="password"
                    aria-label="Default"
                    aria-describedby="inputGroup-sizing-default"
                    autoComplete="new-password"
                    onChange={handleUpdatePinDataChange}
                  />
              </InputGroup>
              <InputGroup className="mb-3">
                  <InputGroup.Prepend>
                      <InputGroup.Text
                          id="inputGroup-sizing-default">{t('certificates.confirm-pin')}:
                      </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name='confirmNewPin'
                    value={confirmNewPin}
                    required
                    type="password"
                    aria-label="Default"
                    aria-describedby="inputGroup-sizing-default"
                    autoComplete="new-password"
                    onChange={handleUpdatePinDataChange}
                  />
              </InputGroup>
            </Modal.Body>
            <Modal.Footer>
              <Button className="reject" onClick={handleHide}>
                  {t('general.cancel')}
              </Button>
              <Button variant="primary" disabled={isUpdatePinButtonDisabled || loading}
                  onClick={handleUpdatePin}>
                  {t('general.confirm')}
              </Button>
            </Modal.Footer>
          </>
        }
      </Modal>
      <Modal show={showCreateNewCertificateModal} onHide={handleCloseCreateNewCertificateModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('certificates.create-certificate')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(KVpwIsInvalid) &&
            <div className="error-container">
              {KVpwIsInvalid ? errorMessageKVpw : ''}
            </div>
          }
          <div className="kv-body">
            <Form.Group
              controlId="formFile"
              style={{ marginBottom: 15 }}
            >
              <input
                id="kv-cert-file"
                type="file"
                accept=".p12, .pfx"
                onChange={(e) => onHandleFileSelection(e)}
                ref={onFileInput}
                style={{ display: 'none' }}
              />
              <div className="kv-file-select">
                <InputGroup.Prepend>
                  <InputGroup.Text
                    id="key-vault-cert-alias">{fileName || t('general.form-file-label')}</InputGroup.Text>
                </InputGroup.Prepend>
                <IconButton
                  icon={FileIcon}
                  tooltipText={t('general.form-file-browse')}
                  onClick={() => document.getElementById('kv-cert-file')?.click()}
                />
              </div>
              <Form.Text className="text-muted">
                {t('certificates.certificate-select')}
              </Form.Text>
            </Form.Group>
            <InputGroup className="mb-3">
              <InputGroup.Prepend>
                <InputGroup.Text
                  id="key-vault-cert-alias">{t('general.alias')}</InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                required
                type="text"
                aria-label="Default"
                aria-describedby="key-vault-cert-alias"
                value={KValias}
                onChange={e => onHandleKeyVaultChange("KValias", e.target.value, null)}
              />
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Prepend>
                <InputGroup.Text
                  id="key-vault-cert-pin">{t('general.pin')}</InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                required
                type="password"
                aria-label="Default"
                aria-describedby="key-vault-cert-pin"
                value={KVpassword}
                onChange={e => onHandleKeyVaultChange("KVpassword", e.target.value, false)}
              />
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Prepend>
                <InputGroup.Text
                  id="key-vault-cert-repeat-pin">{t('certificates.certificate-repeat-pin')}</InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                required
                type="password"
                aria-label="Default"
                aria-describedby="key-vault-cert-repeat-pin"
                value={KVpasswordConfirmation}
                onChange={e => onHandleKeyVaultChange("KVpasswordConfirmation", e.target.value, false)}
              />
            </InputGroup>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary"
              onClick={onUploadCertificateKeyVault}>
              {t('certificates.certificate-upload')}
          </Button>
          </Modal.Footer>
      </Modal>
      {KeyVault.filter(cert => cert.CertType === 'KeyVault' && cert.certalias !== '---').map(cert => {
        const { oauthTokenB64 } = parentState;
        const userGuid = oauthTokenB64 ? Utils.getViDMobileUserGUIDFromOauthToken(oauthTokenB64) : null;
        
        if (!userGuid) return null;
        
        return (
          <CreateVC
            key={cert.GUID}
            vidMobileUserGuid={userGuid}
            certificateGuid={cert.GUID}
            certificateType="KeyVault"
            trigger={createVCTriggers[cert.GUID]}
            onCredentialCreated={onRefreshCertificates}
            credentialNamespace={credentialNamespace}
          />
        );
      })}

      {walletToggleSuccess && (
        <Toast
          onClose={() => setWalletToggleSuccess(null)}
          show={!!walletToggleSuccess}
          delay={3000}
          autohide
          animation={true}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            margin: '0 auto',
            backgroundColor: 'rgba(255,255,255,1)',
            border: 'none',
            zIndex: 9999,
          }}
        >
          <Toast.Header closeButton={false}>
            <strong className="mr-auto">{t('certificates.wallet-credential-revoked')}</strong>
          </Toast.Header>
          <Toast.Body>{t('certificates.wallet-credential-revoked-text')}</Toast.Body>
        </Toast>
      )}
      {walletToggleError && (
        <Toast
          onClose={() => setWalletToggleError(null)}
          show={!!walletToggleError}
          animation={true}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            margin: '0 auto',
            backgroundColor: 'rgba(255,255,255,1)',
            border: 'none',
            zIndex: 9999,
          }}
        >
          <Toast.Header closeButton={true}>
            <strong className="mr-auto">{t('general.error')}</strong>
          </Toast.Header>
          <Toast.Body>{walletToggleError}</Toast.Body>
        </Toast>
      )}
    </>
  )
}

export default KeyVaultCertificates
